import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Lock, Eye, FileText, Users, AlertTriangle } from 'lucide-react';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import { useTranslation } from 'react-i18next';

const Ethics = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('principles');

  const secondaryNav = [
    { id: 'principles', label: t('ethics.principles'), icon: Shield },
    { id: 'consent', label: t('ethics.consent'), icon: Lock },
    { id: 'privacy', label: t('ethics.privacy'), icon: Eye },
    { id: 'governance', label: t('ethics.governance'), icon: FileText },
    { id: 'safety', label: t('ethics.safety'), icon: AlertTriangle },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-black text-white min-h-screen">
      <PageTitle title="Ethics & Governance - Our Principles" />
      
      {/* Secondary Navigation - Sticky */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-black/80 backdrop-blur-md border-b border-cyan-500/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6 overflow-x-auto py-4 no-scrollbar flex-1">
              {secondaryNav.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all duration-200 ${
                      activeSection === item.id
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                        : 'text-gray-400 hover:text-white hover:bg-gray-900/50'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="pl-6 border-l border-gray-700">
              <a href="/ethics/consent" className="text-xs text-cyan-400 hover:text-cyan-300 whitespace-nowrap">
                {t('ethics.viewDetailedPages')}
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* BLOCK 1 - HERO */}
      <section id="principles" className="pt-32 pb-24 lg:pt-40 lg:pb-32 bg-gradient-to-b from-black via-[#0B1F2E]/50 to-black">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h1 className="text-5xl lg:text-7xl font-bold mb-8 reveal-on-scroll">
              {t('ethics.title')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto reveal-on-scroll mb-8">
              {t('ethics.mainSubtitle')}
            </p>
          </div>

          {/* Highlights Box */}
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/30 rounded-xl p-8 reveal-on-scroll">
            <h3 className="text-lg font-semibold text-white mb-4">{t('ethics.corePrinciplesTitle')}</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <span className="text-cyan-400 mt-1">✓</span>
                <p className="text-gray-300">{t('ethics.explicitConsent')}</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-cyan-400 mt-1">✓</span>
                <p className="text-gray-300">{t('ethics.simulationNotReplacement')}</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-cyan-400 mt-1">✓</span>
                <p className="text-gray-300">{t('ethics.prohibitedHighRisk')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 2 - CONSENT & RIGHTS */}
      <section id="consent" className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal-on-scroll">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-6 energy-pulse">
                <FileText className="text-cyan-400" size={40} />
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">{t('ethics.consentComesFirst')}</h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                {t('ethics.consentDescription')}
              </p>
            </div>

            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30 reveal-on-scroll">
              <CardContent className="p-10 space-y-4">
                <h3 className="text-2xl font-bold mb-6">{t('ethics.consentRequirementsTitle')}</h3>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">✓</span>
                    <span>{t('ethics.documentedConsent')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">✓</span>
                    <span>{t('ethics.definedScope')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">✓</span>
                    <span>{t('ethics.clearUsagePurposes')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">✓</span>
                    <span>{t('ethics.respectRights')}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* BLOCK 3 - SIMULATION NOT REPLACEMENT */}
      <section id="safety" className="py-24 lg:py-32 bg-[#0B1F2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-8 reveal-on-scroll">
            {t('ethics.simulationTitle')}
          </h2>
          <p className="text-xl text-gray-300 text-center mb-12 max-w-4xl mx-auto reveal-on-scroll">
            {t('ethics.simulationDescription')}
          </p>

          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-to-br from-cyan-900/20 to-cyan-800/10 border-cyan-500/50 shadow-xl shadow-cyan-500/20 reveal-on-scroll">
              <CardContent className="p-12 text-center">
                <AlertTriangle className="text-cyan-400 mx-auto mb-6" size={48} />
                <h3 className="text-2xl font-bold mb-4 text-cyan-400">{t('ethics.alwaysVisibleNotice')}</h3>
                <p className="text-xl text-white italic leading-relaxed">
                  {t('ethics.simulationNotice')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* BLOCK 4 - PROHIBITED USES */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-8 reveal-on-scroll">
            {t('ethics.prohibitedUsesTitle')}
          </h2>
          <p className="text-xl text-gray-300 text-center mb-16 max-w-4xl mx-auto reveal-on-scroll">
            {t('ethics.prohibitedUsesDesc')}
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              t('ethics.noIdentityFraud'),
              t('ethics.noFinancialInstructions'),
              t('ethics.noMedicalLegalAdvice'),
              t('ethics.noPoliticalPropaganda'),
              t('ethics.noHiddenUse')
            ].map((item, idx) => (
              <Card 
                key={idx} 
                className="bg-gradient-to-br from-gray-900 to-gray-800 border-red-500/30 hover:border-red-500/60 transition-all duration-300 reveal-on-scroll"
                style={{ transitionDelay: `${idx * 50}ms` }}
              >
                <CardContent className="p-8 flex items-start space-x-4">
                  <Lock className="text-red-400 flex-shrink-0 mt-1" size={24} />
                  <p className="text-lg text-gray-300">{item}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 5 - PRIVACY & DATA PROTECTION */}
      <section id="privacy" className="py-24 lg:py-32 bg-[#0B1F2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-6 mx-auto energy-pulse">
              <Shield className="text-cyan-400" size={40} />
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 reveal-on-scroll">
              {t('ethics.privacySecurityTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Lock,
                title: t('ethics.encryption'),
                description: t('ethics.encryptionDesc')
              },
              {
                icon: Eye,
                title: t('ethics.accessControls'),
                description: t('ethics.accessControlsDesc')
              },
              {
                icon: Shield,
                title: t('ethics.dataMinimization'),
                description: t('ethics.dataMinimizationDesc')
              },
              {
                icon: FileText,
                title: t('ethics.rightToDelete'),
                description: t('ethics.rightToDeleteDesc')
              }
            ].map((item, idx) => (
              <Card 
                key={idx} 
                className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 reveal-on-scroll lift-on-hover"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-8 text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl energy-pulse">
                    <item.icon className="text-cyan-400" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 6 - EVALUATION & FEEDBACK */}
      <section id="governance" className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30 reveal-on-scroll">
              <CardContent className="p-10">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-6 energy-pulse">
                  <Users className="text-cyan-400" size={40} />
                </div>
                <h2 className="text-4xl font-bold mb-6">{t('ethics.evaluationTitle')}</h2>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {t('ethics.evaluationDescription')}
                </p>
              </CardContent>
            </Card>

            <div className="space-y-6 reveal-on-scroll">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-cyan-400 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">{t('ethics.rateSimilarity')}</h4>
                  <p className="text-gray-400">{t('ethics.rateSimilarityDesc')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-cyan-400 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">{t('ethics.flagInaccuracies')}</h4>
                  <p className="text-gray-400">{t('ethics.flagInaccuraciesDesc')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-cyan-400 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">{t('ethics.requestCorrections')}</h4>
                  <p className="text-gray-400">{t('ethics.requestCorrectionsDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Ethics;
